import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AppConfiguration } from "@/types/application";
import { toast } from "sonner";

export const useAppConfigurations = (applicationId?: string) => {
  const queryClient = useQueryClient();

  const { data: configurations, isLoading } = useQuery({
    queryKey: ['app-configurations', applicationId],
    queryFn: async () => {
      if (!applicationId) return [];
      
      const { data, error } = await supabase
        .from('app_configurations')
        .select('*')
        .eq('application_id', applicationId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      return (data || []).map((config: any) => ({
        id: config.id,
        applicationId: config.application_id,
        name: config.name,
        description: config.description,
        configurationData: config.configuration_data,
        customers: config.customers || [],
        isActive: config.is_active,
        createdAt: config.created_at,
        updatedAt: config.updated_at,
        createdBy: config.created_by,
      })) as AppConfiguration[];
    },
    enabled: !!applicationId
  });

  const createConfiguration = async (config: Omit<AppConfiguration, 'id' | 'createdAt' | 'updatedAt'>) => {
    const { data: { user } } = await supabase.auth.getUser();
    
    const { data, error } = await supabase
      .from('app_configurations')
      .insert([{
        application_id: config.applicationId,
        name: config.name,
        description: config.description,
        configuration_data: config.configurationData as any,
        customers: config.customers,
        is_active: config.isActive,
        created_by: user?.id,
      }])
      .select()
      .single();

    if (error) throw error;
    
    queryClient.invalidateQueries({ queryKey: ['app-configurations'] });
    toast.success('App configuration created successfully');
    return data;
  };

  const updateConfiguration = async (id: string, config: Partial<AppConfiguration>) => {
    const updateData: any = {};
    
    if (config.name !== undefined) updateData.name = config.name;
    if (config.description !== undefined) updateData.description = config.description;
    if (config.configurationData !== undefined) updateData.configuration_data = config.configurationData as any;
    if (config.customers !== undefined) updateData.customers = config.customers;
    if (config.isActive !== undefined) updateData.is_active = config.isActive;

    const { data, error } = await supabase
      .from('app_configurations')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    
    queryClient.invalidateQueries({ queryKey: ['app-configurations'] });
    toast.success('App configuration updated successfully');
    return data;
  };

  const deleteConfiguration = async (id: string) => {
    const { error } = await supabase
      .from('app_configurations')
      .delete()
      .eq('id', id);

    if (error) throw error;
    
    queryClient.invalidateQueries({ queryKey: ['app-configurations'] });
    toast.success('App configuration deleted successfully');
  };

  const toggleConfiguration = async (id: string, isActive: boolean) => {
    const { error } = await supabase
      .from('app_configurations')
      .update({ is_active: isActive })
      .eq('id', id);

    if (error) throw error;
    
    queryClient.invalidateQueries({ queryKey: ['app-configurations'] });
    toast.success(`App configuration ${isActive ? 'activated' : 'deactivated'}`);
  };

  return {
    configurations: configurations || [],
    isLoading,
    createConfiguration,
    updateConfiguration,
    deleteConfiguration,
    toggleConfiguration,
  };
};
